import { checked, conditional, integer, packageSchemaForFormik, required } from '@ahmdigital/form-validation';
import { FormikErrors } from 'formik';
import { has, includes, isEmpty, pickBy, reduce, set, some } from 'lodash/fp';
import { Schema } from '@ahmdigital/form-validation/lib/validators/types';

import { Country, FormValues } from '../types';
import { WHO_FIELD_NAMES } from '../constants';
import getCountriesBySearch from '../utils/get-countries-by-search';
import getMaximumChildrenAndAdults from '../utils/get-maximum-children-and-adults';

type TravellerAgeFields = {
  [key: string]: string;
};

const MAX_AGE = 120;
const VALID_AGE_MESSAGE = 'Enter a valid age';

const hasErrors = (fieldNames: string[], errors: FormikErrors<FormValues> | null): boolean =>
  some((fieldName) => has(fieldName, errors), fieldNames);

const getTravellerFieldsData = (allFormValues: FormValues) => {
  const travellerAgeFields = pickBy((_, key) => includes('travelerAge_', key), allFormValues) as TravellerAgeFields;
  return getMaximumChildrenAndAdults(travellerAgeFields);
};

const getTravellerFields = (travellerCount: number) => {
  const defaultValue = {};
  const arrayToIterate = Array.from({ length: travellerCount }, (_, i) => i + 1);
  return reduce(
    (result, value) =>
      set(
        `travelerAge_${value}`,
        {
          initialValue: '',
          validation: [
            required(VALID_AGE_MESSAGE),
            integer('Enter a valid number'),
            conditional(
              () => !!value,
              [
                // @ts-expect-error [🤖 PLEASE FIX 🤖]: Type '(age: number) => "Enter a valid age" | undefined' is not assignable to type 'Validator'.
                (age: number) => (age < 0 ? VALID_AGE_MESSAGE : undefined),
                // @ts-expect-error [🤖 PLEASE FIX 🤖]: Type '(age: number) => "Enter a valid age" | undefined' is not assignable to type 'Validator'.
                (age: number) => (age > MAX_AGE ? VALID_AGE_MESSAGE : undefined),
                // @ts-expect-error [🤖 PLEASE FIX 🤖]: Type '(_: string, allFormValues: FormValues) => "If you are travelling with more than 4 adults please contact our Customer Service team on 1300 617 409 to discuss your travel insurance options." | undefined' is not assignable to type 'Validator'.
                (_: string, allFormValues: FormValues) => {
                  const { amountOfAdults, indexOfAdultMaxAge } = getTravellerFieldsData(allFormValues);
                  if (amountOfAdults > 4 && value === indexOfAdultMaxAge) {
                    return 'If you are travelling with more than 4 adults please contact our Customer Service team on 1300 617 409 to discuss your travel insurance options.';
                  }
                  return undefined;
                },
                // @ts-expect-error [🤖 PLEASE FIX 🤖]: Type '(_: string, allFormValues: FormValues) => "We are unable to provide a quote due to the combination of adults and children listed, please contact us on 1300 617 409 to discuss your options." | undefined' is not assignable to type 'Validator'.
                (_: string, allFormValues: FormValues) => {
                  const { amountOfChildren, indexOfChildrenMaxAge } = getTravellerFieldsData(allFormValues);
                  if (amountOfChildren > 6 && value === indexOfChildrenMaxAge) {
                    return 'We are unable to provide a quote due to the combination of adults and children listed, please contact us on 1300 617 409 to discuss your options.';
                  }
                  return undefined;
                },
              ],
            ),
          ],
        },
        result,
      ),
    defaultValue,
    arrayToIterate,
  );
};

const generateSchema = <T extends FormValues>({
  countries,
  travellerCount,
  widgetErrors,
}: {
  countries: Country[];
  widgetErrors: FormikErrors<T> | null;
  travellerCount: number;
}) => {
  const travellerCountWithPrimary = travellerCount + 1;
  const schema: Schema = {
    departureDate: {
      initialValue: '',
    },
    destinations: {
      initialValue: '',
    },
    isMember: {
      initialValue: false,
    },
    promoCode: {
      initialValue: '',
    },
    resident: {
      initialValue: false,
      validation: [
        required(),
        checked(
          'Please check the box to confirm all travellers hold a valid Australian Medicare card or visa allowing them to live, work or study in Australia. Otherwise, contact our Customer Service team on 1300 617 409 to discuss.',
        ),
      ],
    },
    returnDate: {
      initialValue: '',
    },
    when: {
      initialValue: '',
      validation: [
        conditional(
          // @ts-expect-error [🤖 PLEASE FIX 🤖]: Argument of type '({ departureDate, returnDate }: Pick<T, 'departureDate' | 'returnDate'>) => boolean' is not assignable to parameter of type 'ConditionalRule'.
          ({ departureDate, returnDate }: Pick<T, 'departureDate' | 'returnDate'>) => !departureDate || !returnDate,
          [(value: string) => (!value ? 'Fill in your travel dates to continue.' : undefined)],
        ),
      ],
    },
    where: {
      initialValue: '',
      validation: [
        // @ts-expect-error [🤖 PLEASE FIX 🤖]: Type 'Validator' is not assignable to type 'NestedValidator'.
        required('Add your destination to continue.'),
        conditional(
          // @ts-expect-error [🤖 PLEASE FIX 🤖]: Argument of type '({ destinations, where }: Pick<T, 'destinations' | 'where'>) => boolean' is not assignable to parameter of type 'ConditionalRule'.
          ({ destinations, where }: Pick<T, 'destinations' | 'where'>) =>
            isEmpty(destinations) || isEmpty(getCountriesBySearch(countries, where)),
          [() => 'Select destination from dropdown.'],
        ),
      ],
    },
    who: {
      initialValue: '',
      validation: [
        conditional(
          () => hasErrors(WHO_FIELD_NAMES, widgetErrors),
          [() => 'Enter your traveller details to continue.'],
        ),
      ],
    },
    ...getTravellerFields(travellerCountWithPrimary),
  };

  return { packagedSchema: packageSchemaForFormik(schema), rawSchema: schema };
};

export default generateSchema;
